import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Task } from '@crossmatch/shared';
import { tasksApi } from '../api/tasks.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { EmptyState } from '../components/EmptyState.js';
import { ErrorState } from '../components/ErrorState.js';
import { Loading } from '../components/Loading.js';
import { Tag } from '../components/Tag.js';
import { formatDue } from '../lib/format.js';
import { NEXT_TASK_STATUS, TASK_STATUS_LABELS, TASK_STATUS_ORDER, taskPhaseLabel } from '../lib/labels.js';
import { qk } from '../lib/queryKeys.js';
import { useMyTasksQuery, useMyTeamsQuery } from '../hooks/queries.js';
import { toast } from '../store/toast.js';

const COLUMN_HINT: Record<Task['status'], string> = {
  todo: '点卡片 → 推进到「进行中」',
  doing: '点卡片 → 推进到「已完成」',
  done: '点卡片 → 退回「待开始」',
};

/** 阶段待办看板：三列（todo/doing/done），点卡片推进状态（PATCH /tasks/:id）。 */
export function TasksPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const projectFilter = searchParams.get('project') ?? 'all';
  const queryClient = useQueryClient();

  const tasksQuery = useMyTasksQuery();
  const teamsQuery = useMyTeamsQuery();

  const teamNameByProject = useMemo(() => {
    const map = new Map<string, string>();
    for (const team of teamsQuery.data?.teams ?? []) {
      if (typeof team.projectId === 'string' && team.projectId.length > 0) map.set(team.projectId, team.name);
    }
    return map;
  }, [teamsQuery.data]);

  const projectOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const task of tasksQuery.data?.tasks ?? []) {
      if (!map.has(task.projectId)) map.set(task.projectId, teamNameByProject.get(task.projectId) ?? task.projectId);
    }
    return [...map.entries()].map(([id, label]) => ({ id, label }));
  }, [tasksQuery.data, teamNameByProject]);

  const updateTask = useMutation({
    mutationFn: (input: { task: Task; status: Task['status'] }) => tasksApi.update(input.task.id, { status: input.status }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: qk.myTasks() });
      const updated = data?.task;
      if (!updated || typeof updated.status !== 'string') {
        toast.error('状态已提交，但后端没有返回 task', '已刷新看板，请以刷新后的状态为准');
        return;
      }
      toast.success(`已更新：${updated.title}`, `状态 → ${TASK_STATUS_LABELS[updated.status]}`);
    },
    // 失败提示统一由 App.tsx 的全局 MutationCache.onError 负责（原先这里还会弹一次
    // 「更新待办状态失败」，与全局那条重复；已按 task-13 移除，避免同一次失败弹两次）。
  });

  const allTasks = tasksQuery.data?.tasks ?? [];
  const visibleTasks = projectFilter === 'all' ? allTasks : allTasks.filter((task) => task.projectId === projectFilter);

  const handleFilterChange = (value: string): void => {
    if (value === 'all') {
      setSearchParams({});
      return;
    }
    setSearchParams({ project: value });
  };

  if (tasksQuery.isPending) {
    return (
      <div className="stack">
        <div className="page-head">
          <h1 className="page-title">阶段待办</h1>
        </div>
        <Loading label="正在读取我的待办…" rows={4} />
      </div>
    );
  }

  if (tasksQuery.isError) {
    return (
      <div className="stack">
        <div className="page-head">
          <h1 className="page-title">阶段待办</h1>
        </div>
        <ErrorState error={tasksQuery.error} title="读取待办失败" onRetry={() => void tasksQuery.refetch()} />
      </div>
    );
  }

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-title">阶段待办</h1>
          <p className="page-sub">待办在成队时自动生成；点击卡片在三列间推进状态。</p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => void tasksQuery.refetch()}>
          刷新
        </Button>
      </div>

      {allTasks.length === 0 ? (
        <EmptyState
          icon="☐"
          title="还没有阶段待办"
          description="待办会在「确认成队」时由后端自动生成 4 条（阶段 1–4）。先去我的队伍里确认成队，再回来看看。"
          action={
            <Link className="btn btn-primary" to="/team">
              去我的队伍确认成队
            </Link>
          }
        />
      ) : (
        <>
          <Card title="筛选与说明" subtitle={`共 ${allTasks.length} 条待办；点击卡片在三列间推进状态。`}>
            <div className="row">
              <div className="field" style={{ maxWidth: '300px' }}>
                <label className="field-label" htmlFor="task-project-filter">
                  按项目筛选
                </label>
                <select
                  id="task-project-filter"
                  className="select"
                  value={projectFilter}
                  onChange={(event) => handleFilterChange(event.target.value)}
                >
                  <option value="all">全部项目（{allTasks.length} 条）</option>
                  {projectOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <span className="field-hint">
                {teamsQuery.isError
                  ? '队伍名称读取失败，项目列显示的是 projectId（不影响待办操作）。'
                  : '项目列显示为队伍名称；点卡片推进状态会立即写入后端。'}
              </span>
            </div>
          </Card>

          {visibleTasks.length === 0 ? (
            <EmptyState
              icon="∅"
              title="当前筛选下没有待办"
              description="换一个项目，或点「全部项目」查看所有待办。"
              action={
                <Button variant="secondary" onClick={() => handleFilterChange('all')}>
                  查看全部项目
                </Button>
              }
            />
          ) : (
            <div className="kanban">
              {TASK_STATUS_ORDER.map((status) => {
                const columnTasks = visibleTasks.filter((task) => task.status === status);
                return (
                  <section className="kanban-col" key={status} aria-label={`${TASK_STATUS_LABELS[status]}列`}>
                    <header className="kanban-col-head">
                      <Tag tone={status === 'done' ? 'ok' : status === 'doing' ? 'primary' : 'muted'}>
                        {TASK_STATUS_LABELS[status]}
                      </Tag>
                      <span className="kanban-count">{columnTasks.length} 条</span>
                    </header>
                    <p className="field-hint">{COLUMN_HINT[status]}</p>

                    {columnTasks.length === 0 ? (
                      <p className="muted small">本列暂时没有任务。</p>
                    ) : (
                      columnTasks.map((task) => {
                        const nextStatus = NEXT_TASK_STATUS[task.status];
                        const pending = updateTask.isPending && updateTask.variables?.task.id === task.id;
                        const projectLabel = teamNameByProject.get(task.projectId);
                        return (
                          <button
                            key={task.id}
                            type="button"
                            className="task-card"
                            disabled={pending}
                            title={`点击推进到「${TASK_STATUS_LABELS[nextStatus]}」`}
                            onClick={() => updateTask.mutate({ task, status: nextStatus })}
                          >
                            <span className="task-title">{task.title}</span>
                            <span className="task-meta">
                              <Tag tone="primary">{taskPhaseLabel(task.phase)}</Tag>
                              {projectLabel ? <Tag>{projectLabel}</Tag> : <Tag tone="muted">{task.projectId}</Tag>}
                            </span>
                            <span className="task-meta">
                              <span>{formatDue(task.dueDate)}</span>
                              <span>负责人：{task.ownerId ?? '未指派'}</span>
                              {pending ? <span>· 正在更新…</span> : null}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}